/* Copyright (c) 2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useMemo, useState } from "react";
import { ApolloQueryResult, OperationVariables } from "@apollo/client";

export const REQUEST_LIMIT = 50;

type Props<T, R> = {
  firstPageLoading: boolean;
  firstPageResult: R | undefined;
  pageInfo:
    | {
        pages: number;
        count: number;
      }
    | undefined;
  fetchMore: (opts: OperationVariables) => Promise<ApolloQueryResult<R>>;
  fetchLimit?: number;
  mapFunc: (data: R) => T[];
};

export function useFetchAllPages<T, R>({
  firstPageLoading,
  firstPageResult,
  pageInfo = { pages: 0, count: 0 },
  fetchLimit = REQUEST_LIMIT,
  fetchMore,
  mapFunc,
}: Props<T, R>) {
  const [items, setItems] = useState<T[]>([]);
  const [fetchMoreLoading, setFetchMoreLoading] = useState(false);

  const firstPageItems = useMemo(
    () => (firstPageResult ? mapFunc(firstPageResult) : []),
    [firstPageResult, mapFunc]
  );

  const { pages = 0, count = 0 } = pageInfo;

  const shouldFetchMore =
    !firstPageLoading && pages > 1 && count > items.length;

  const loading = firstPageLoading || fetchMoreLoading || shouldFetchMore;

  useEffect(() => {
    setItems(firstPageItems);
  }, [firstPageItems]);

  useEffect(() => {
    if (firstPageLoading) {
      return;
    }

    if (!shouldFetchMore) {
      setFetchMoreLoading(false);
      return;
    }

    let cancelled = false;
    setFetchMoreLoading(true);

    const pageNumbers = Array.from({ length: pages - 1 }, (_, idx) => idx + 2);

    Promise.all(
      pageNumbers.map((page) =>
        fetchMore({
          variables: {
            page,
            limit: fetchLimit,
          },
        }).then(({ data }) => mapFunc(data))
      )
    )
      .then((mappedPages) => {
        if (cancelled) {
          return;
        }

        const appendedItems = mappedPages.flat();
        return setItems((prev) => [...prev, ...appendedItems]);
      })
      .finally(() => {
        if (!cancelled) {
          setFetchMoreLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    firstPageLoading,
    pages,
    shouldFetchMore,
    fetchMore,
    fetchLimit,
    mapFunc,
  ]);

  return { items, loading };
}
